import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Content,
  Header,
  HeaderName,
  HeaderGlobalBar,
  Stack,
  Heading,
  Theme,
  Loading,
  Grid,
  Column,
} from '@carbon/react';
import AccountTypeSelector from '../../components/auth/AccountTypeSelector';
import CarbonEnhancedRegistrationForm from '../../components/auth/CarbonEnhancedRegistrationForm';

interface RegistrationPageProps {
  accountType?: 'individual' | 'family' | 'corporate' | 'school';
}

const RegistrationPage: React.FC<RegistrationPageProps> = ({ accountType: presetAccountType }) => {
  const navigate = useNavigate();
  const [selectedAccountType, setSelectedAccountType] = useState<string>(presetAccountType || '');
  const [registrationRequirements, setRegistrationRequirements] = useState<any>(null);
  const [error, setError] = useState<string>('');

  // Fetch requirements when preset account type is provided
  React.useEffect(() => {
    if (presetAccountType) {
      // Immediately set the account type to show the form
      setSelectedAccountType(presetAccountType);

      // Set default requirements immediately to avoid loading state
      const defaultRequirements = {
        accountType: presetAccountType,
        fields: ['email', 'password', 'fullName'],
        features: [],
      };
      setRegistrationRequirements(defaultRequirements);

      // Try to fetch enhanced requirements in the background
      const fetchRequirements = async () => {
        try {
          const apiUrl = import.meta.env.VITE_API_BASE_URL || 'https://staging.sigma08.com';

          // Create AbortController for timeout
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 3000);

          const response = await fetch(`${apiUrl}/api/v1/accounts/validate-type`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ accountType: presetAccountType }),
            signal: controller.signal,
          });

          clearTimeout(timeoutId);

          if (response.ok) {
            const data = await response.json();
            // Only update if we got better data
            if (data.requirements || data.fields) {
              setRegistrationRequirements(data.requirements || data);
            }
          }
        } catch (err) {
          // Silently fail - we already have default requirements
          console.warn('Failed to fetch account type requirements, using defaults:', err);
        }
      };

      // Fetch in background without blocking UI
      fetchRequirements();
    }
  }, [presetAccountType]);

  const handleAccountTypeSelected = (accountType: string, requirements: any) => {
    setSelectedAccountType(accountType);
    setRegistrationRequirements(requirements);

    // Navigate to the specific registration form based on account type
    switch (accountType) {
      case 'individual':
        navigate('/register/individual', { state: { requirements } });
        break;
      case 'family':
        navigate('/register/family', { state: { requirements } });
        break;
      case 'corporate':
        navigate('/register/corporate', { state: { requirements } });
        break;
      case 'school':
        navigate('/register/school', { state: { requirements } });
        break;
      default:
        break;
    }
  };

  const handleRegistrationSuccess = (user: any) => {
    // Redirect to appropriate dashboard based on user role
    navigate('/dashboard');
  };

  const renderRegistrationForm = () => {
    switch (selectedAccountType) {
      case 'individual':
        return (
          <CarbonEnhancedRegistrationForm
            accountType="individual"
            requirements={registrationRequirements}
            onSuccess={handleRegistrationSuccess}
            onCancel={() => {
              setSelectedAccountType('');
              navigate('/register/select-type');
            }}
          />
        );
      case 'family':
        return (
          <CarbonEnhancedRegistrationForm
            accountType="family"
            requirements={registrationRequirements}
            onSuccess={handleRegistrationSuccess}
            onCancel={() => {
              setSelectedAccountType('');
              navigate('/register/select-type');
            }}
          />
        );
      case 'corporate':
        return (
          <CarbonEnhancedRegistrationForm
            accountType="corporate"
            requirements={registrationRequirements}
            onSuccess={handleRegistrationSuccess}
            onCancel={() => {
              setSelectedAccountType('');
              navigate('/register/select-type');
            }}
          />
        );
      case 'school':
        return (
          <CarbonEnhancedRegistrationForm
            accountType="school"
            requirements={registrationRequirements}
            onSuccess={handleRegistrationSuccess}
            onCancel={() => {
              setSelectedAccountType('');
              navigate('/register/select-type');
            }}
          />
        );
      default:
        return <AccountTypeSelector onAccountTypeSelected={handleAccountTypeSelected} error={error} />;
    }
  };

  return (
    <Theme theme="g100">
      <Header aria-label="Sigma08 Platform">
        <HeaderName prefix="Sigma08">Learning Platform</HeaderName>
        <HeaderGlobalBar />
      </Header>
      <Content>
        <Grid
          className="registration-page-grid"
          style={{
            minHeight: 'calc(100vh - 48px)',
            paddingTop: 'var(--cds-spacing-07)',
            paddingBottom: 'var(--cds-spacing-07)',
            background: 'var(--cds-background)',
          }}
        >
          <Column
            sm={4}
            md={6}
            lg={8}
            xlg={6}
            max={6}
            className="registration-form-column"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {renderRegistrationForm()}
          </Column>
        </Grid>
      </Content>
    </Theme>
  );
};

export default RegistrationPage;
